import { useMemo } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import { FormattedRpServiceConfigurationServiceOffering } from "@idw-types/data/rp-service-configuration";
import { SelectOption } from "@idw-types/common";
import { LabelWidthTypes } from "@enums/label-width-types";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { LabelValueContainer } from "../label-value-container/label-value-container";
import { LayoutTypes } from "@enums/layout-types";
import { SelectFormGroup } from "../select-form-group/select-form-group";
import { resources } from "@constants/hardcoded-strings";
import { useModalContext } from "@context/use-modal-context";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { ModalTypes } from "@enums/modal-types";
import { ConfirmAddBackupServiceModal } from "../../modals/confirm-add-backup-service-modal/confirm-add-backup-service-modal";
import { AddBackupServiceModal } from "../../modals/add-backup-service-modal/add-backup-service-modal";
import styles from "./rp-service-configuration-service-offering-container.less";

type RpServiceConfigurationServiceOfferingContainerProps = {
    isDisabled: boolean;
    canModifyFallbackServiceOfferings: boolean;
    canViewFallbackServiceOfferings: boolean;
    rpServiceConfigurationServiceOffering: FormattedRpServiceConfigurationServiceOffering;
    rpServiceConfigurationServiceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>;
    removeServiceOffering: (rpServiceConfigurationServiceOffering: FormattedRpServiceConfigurationServiceOffering) => void;
    setServiceOfferingDataSource: (rpServiceConfigurationServiceOffering: FormattedRpServiceConfigurationServiceOffering, newDataSource: string) => void;
    addFallbackServiceOffering: (
        rpServiceConfigurationServiceOffering: FormattedRpServiceConfigurationServiceOffering,
        fallbackServiceOffering: FormattedRpServiceConfigurationServiceOffering,
        fallbackServiceOfferingDataSource: string
    ) => void;
    removeFallbackServiceOffering: (
        rpServiceConfigurationServiceOffering: FormattedRpServiceConfigurationServiceOffering,
        fallbackServiceOffering: FormattedRpServiceConfigurationServiceOffering
    ) => void;
};

export const RpServiceConfigurationServiceOfferingContainer = (props: RpServiceConfigurationServiceOfferingContainerProps) => {
    const {
        isDisabled,
        canModifyFallbackServiceOfferings,
        canViewFallbackServiceOfferings,
        rpServiceConfigurationServiceOffering,
        rpServiceConfigurationServiceOfferings,
        removeServiceOffering,
        setServiceOfferingDataSource,
        addFallbackServiceOffering,
        removeFallbackServiceOffering
    } = props;

    const modalContext = useModalContext();

    const { dataSourceOptions, selectedDataSourceOption } = useMemo(() => {
        let selectOptions: Array<SelectOption> = [];
        let selectedOption = null;
        if (rpServiceConfigurationServiceOffering.dataSources.length > 0) {
            selectOptions = rpServiceConfigurationServiceOffering.dataSources.map(ds => ({ label: ds.value, value: ds.value }));
            const foundOption = rpServiceConfigurationServiceOffering.dataSources
                .find(ds => ds.value === rpServiceConfigurationServiceOffering.dataSource);

            if (foundOption) {
                selectedOption = { label: foundOption.value, value: foundOption.value };
            }
        }

        return { selectedDataSourceOption: selectedOption, dataSourceOptions: selectOptions };
    }, [rpServiceConfigurationServiceOffering]);

    const { fallbackServiceOfferings, selectedFallbackServiceOffering } = useMemo(() => {
        let selectedServiceOffering = null;
        let availableFallbackServiceOfferings: Array<FormattedRpServiceConfigurationServiceOffering> = [];
        if (rpServiceConfigurationServiceOffering.fallbackServiceOfferingIds.length > 0) {
            availableFallbackServiceOfferings = rpServiceConfigurationServiceOfferings.filter(so =>
                rpServiceConfigurationServiceOffering.fallbackServiceOfferingIds.includes(so.serviceOfferingID) &&
                (!so.addedToService || so.status === EntityStatusTypes.INACTIVE)
            );
        }
        if (rpServiceConfigurationServiceOffering.fallbackServiceOfferingID) {
            selectedServiceOffering = rpServiceConfigurationServiceOfferings.find(so => so.serviceOfferingID === rpServiceConfigurationServiceOffering.fallbackServiceOfferingID) || null;
        } else if (rpServiceConfigurationServiceOffering.fallbackRPServiceSOID) {
            selectedServiceOffering = rpServiceConfigurationServiceOfferings.find(so => so.id === rpServiceConfigurationServiceOffering.fallbackRPServiceSOID) || null;
        }

        return { fallbackServiceOfferings: availableFallbackServiceOfferings, selectedFallbackServiceOffering: selectedServiceOffering };
    }, [rpServiceConfigurationServiceOfferings]);

    const { fallbackDataSourceOptions, selectedFallbackDataSourceOption } = useMemo(() => {
        let selectOptions: Array<SelectOption> = [];
        let selectedOption: SelectOption = null;
        let fallbackService: FormattedRpServiceConfigurationServiceOffering = null;
        if (rpServiceConfigurationServiceOffering.fallbackServiceOfferingID) {
            fallbackService = rpServiceConfigurationServiceOfferings.find(so => so.serviceOfferingID === rpServiceConfigurationServiceOffering.fallbackServiceOfferingID) || null;
        } else if (rpServiceConfigurationServiceOffering.fallbackRPServiceSOID) {
            fallbackService = rpServiceConfigurationServiceOfferings.find(so => so.id === rpServiceConfigurationServiceOffering.fallbackRPServiceSOID) || null;
        }

        if (fallbackService) {
            selectOptions = fallbackService.dataSources.map(ds => ({ label: ds.value, value: ds.value }));
            const foundOption = fallbackService.dataSources.find(ds => ds.value === fallbackService.dataSource);
            if (foundOption) {
                selectedOption = { label: foundOption.value, value: foundOption.value };
            }
        }

        return { selectedFallbackDataSourceOption: selectedOption, fallbackDataSourceOptions: selectOptions };
    }, [fallbackServiceOfferings, selectedFallbackServiceOffering]);

    const onChangePrimaryServiceSelect = (newValue: SelectOption) => {
        setServiceOfferingDataSource(rpServiceConfigurationServiceOffering, newValue?.value);
    };

    const onChangeBackupServiceSelect = (newValue: SelectOption) => {
        setServiceOfferingDataSource(selectedFallbackServiceOffering, newValue?.value);
    }

    const onClickDelete = () => {
        removeServiceOffering(rpServiceConfigurationServiceOffering);
    };

    const onClickContinue = () => {
        const renderAddBackupServiceModal = (onModalClose: () => void) => {
            return (
                <AddBackupServiceModal
                    rpServiceConfigurationServiceOffering={rpServiceConfigurationServiceOffering}
                    rpServiceConfigurationServiceOfferings={fallbackServiceOfferings}
                    addFallbackServiceOffering={addFallbackServiceOffering}
                    onModalClose={onModalClose}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.AddBackupService,
            renderFunction: renderAddBackupServiceModal
        });
    };

    const onClickAddFallbackServiceOffering = () => {
        const renderConfirmAddBackupServiceModal = (onModalClose: () => void) => {
            return (
                <ConfirmAddBackupServiceModal
                    serviceName={rpServiceConfigurationServiceOffering.name}
                    onClickContinue={onClickContinue}
                    onModalClose={onModalClose}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.ConfirmAddBackupService,
            renderFunction: renderConfirmAddBackupServiceModal
        })
    };

    const onClickRemoveFallbackServiceOffering = () => {
        removeFallbackServiceOffering(rpServiceConfigurationServiceOffering, selectedFallbackServiceOffering);
    };

    const resource = resources.pages.verificationServiceDetails.tabs.services;

    return (
        <div className={styles.rpServiceConfigurationServiceOfferingContainer}>
            <LabelValueContainer
                controlId={`service-offering-${rpServiceConfigurationServiceOffering.serviceOfferingID}-label-value`}
                label={rpServiceConfigurationServiceOffering.name}
                labelWidth={LabelWidthTypes.EXTRA_SMALL}
                layout={LayoutTypes.ROW}
                labelIsBold={true}
                value={" "}
                afterValueIcon={isDisabled ? null : faTrash}
                afterValueIconDisabled={isDisabled}
                afterValueIconOnClick={isDisabled ? null : onClickDelete}
                afterValueIconContainerMarginLeftAuto={true}
            />
            {dataSourceOptions.length > 1 && (
                <SelectFormGroup
                    controlId={`service-offering-${rpServiceConfigurationServiceOffering.serviceOfferingID}-data-source-select`}
                    automationId={`service-offering-${rpServiceConfigurationServiceOffering.serviceOfferingID}-data-source-select`}
                    label={resource.subTabs.common.inputs.dataSource.label}
                    ariaLabel={resource.subTabs.common.inputs.dataSource.label}
                    disabled={isDisabled}
                    isClearable={false}
                    isRequired={false}
                    isSearchable={false}
                    labelWidth={LabelWidthTypes.SMALL}
                    onChange={onChangePrimaryServiceSelect}
                    options={dataSourceOptions}
                    selectedOption={selectedDataSourceOption}
                />
            )}
            {(
                canModifyFallbackServiceOfferings &&
                fallbackServiceOfferings.length > 0 &&
                !selectedFallbackServiceOffering &&
                !rpServiceConfigurationServiceOffering.fallbackServiceOfferingID &&
                !rpServiceConfigurationServiceOffering.fallbackRPServiceSOID &&
                !isDisabled
            ) && (
                    <Button id="add-backup-service-button" className={styles.backupServiceButton} onClick={onClickAddFallbackServiceOffering} variant={"outline-secondary"}>
                        {resource.subTabs.common.buttons.addBackupService.label}
                    </Button>
                )}
            {(
                canViewFallbackServiceOfferings &&
                fallbackServiceOfferings.length > 0 &&
                !selectedFallbackServiceOffering &&
                (rpServiceConfigurationServiceOffering.fallbackServiceOfferingID || rpServiceConfigurationServiceOffering.fallbackRPServiceSOID)
            ) && (
                    <Alert variant={"warning"}>{resource.subTabs.common.alerts.invalidFallbackServiceOfferingMessage}</Alert>
                )}
            {(
                canViewFallbackServiceOfferings &&
                selectedFallbackServiceOffering
            ) && (
                    <>
                        <LabelValueContainer
                            controlId={`backup-service-offering-${rpServiceConfigurationServiceOffering.serviceOfferingID}-label-value`}
                            label={resource.subTabs.common.labels.backupService}
                            labelWidth={LabelWidthTypes.EXTRA_SMALL}
                            layout={LayoutTypes.ROW}
                            value={selectedFallbackServiceOffering.name}
                            labelIsBold={true}
                        />
                        {dataSourceOptions.length > 1 && (
                            <SelectFormGroup
                                controlId={`backup-service-offering-${rpServiceConfigurationServiceOffering.serviceOfferingID}-data-source-select`}
                                automationId={`backup-service-offering-${rpServiceConfigurationServiceOffering.serviceOfferingID}-data-source-select`}
                                label={resource.subTabs.common.inputs.dataSource.label}
                                ariaLabel={resource.subTabs.common.inputs.dataSource.label}
                                disabled={isDisabled}
                                isClearable={false}
                                isRequired={false}
                                isSearchable={false}
                                labelWidth={LabelWidthTypes.SMALL}
                                onChange={onChangeBackupServiceSelect}
                                options={fallbackDataSourceOptions}
                                selectedOption={selectedFallbackDataSourceOption}
                            />
                        )}
                        {canModifyFallbackServiceOfferings && !isDisabled && (
                            <Button id="service-offering-remove-backup-service-button" className={styles.backupServiceButton} onClick={onClickRemoveFallbackServiceOffering} variant={"outline-secondary"}>
                                {resource.subTabs.common.buttons.removeBackupService.label}
                            </Button>
                        )}
                    </>
                )}
        </div>
    );
};